import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { StarIcon, CheckCircleIcon } from '@heroicons/react/24/solid'
import {
  ShipmentGroup,
  ShipmentGroupErrors,
  ShipmentPieceRow,
} from '../../interfaces/Import/ShipmentGroupInterface'
import { DeliveryEnum } from '../../interfaces'
import { ServiceDTO } from '../../interfaces/Services/Services'
import {
  FormText,
  FormSelect,
  FormTextArea,
  FormSearch,
  ShipmentBulkUploadPiecesList,
  FavoriteDestinationPicker,
} from '..'
import {
  FormMapsAutocomplete,
  AutocompleteRegion,
} from '../FormFields/FormMaps'
import { useAppSelector } from '../../store/hooks'
import { getStoreByCoverageDjango } from '../../services'
import { getShipmentsExisting } from '../../services/ShipmentBulkUploadServices'
import { DestinationInterface } from '../../interfaces/Account/DestinationInterface'
import {
  applyDestinationFromFavorite,
  clearConsigneeFields,
  reverseGeocodeCoords,
  tryParsePastedPhone,
  COORD_OUT_OF_ZONE_MSG,
} from '../../utils/shipmentImportUtils'
import { obtenerCodigoArea, obtenerNumero } from '../../utils/format'

type OptionLike = {
  id?: any
  name?: string
  value?: any
  locationCode?: string
  locationName?: string
  abbreviationName?: string
  taxIdentificationTypeId?: any
  buCode?: string
  buName?: string
  location?: {
    name?: string
    locationCode?: string
    address?: string
    postalCode?: string
  }
}

// Module-scope filter helper (no closure deps) — stable reference across renders,
// safe to pass as `onFilter` prop without recreating per-row.
const filterByName = (opt: OptionLike, filter: string) => {
  const q = filter.toLowerCase()
  return (
    (opt.name || '').toLowerCase().includes(q) ||
    (opt.abbreviationName || '').toLowerCase().includes(q) ||
    (opt.locationName || '').toLowerCase().includes(q) ||
    (opt.buCode || '').toLowerCase().includes(q) ||
    (opt.buName || '').toLowerCase().includes(q)
  )
}

export const ShipmentBulkUploadAccordionRow: FC<{
  group: ShipmentGroup
  index: number
  errors: ShipmentGroupErrors
  identificationTypes: OptionLike[]
  typeShipment: { value: string; name: string }[]
  businessUnits: OptionLike[]
  cities: OptionLike[]
  citiesByBu?: { locationCode: string; buCode: string }[]
  pieceCategories: OptionLike[]
  isCOD: boolean
  serviceOptions?: ServiceDTO[]
  onChangeGroup: (patch: Partial<ShipmentGroup>) => void
  onChangePiece: (pieceIndex: number, patch: Partial<ShipmentPieceRow>) => void
  onAddPiece: () => void
  onRemovePiece: (pieceIndex: number) => void
  validatePiece: (
    p: ShipmentPieceRow,
    serviceId?: number
  ) => Partial<Record<keyof ShipmentPieceRow, string>>
  onRequestMerge?: () => void
  onCollapsed?: () => void
  open?: boolean
  onToggle?: () => void
  phonePrefixList?: { value: string; text: string }[]
  favoriteDestinations?: DestinationInterface[]
  favsByRif?: Map<string, DestinationInterface>
}> = ({
  group,
  index,
  errors,
  identificationTypes,
  typeShipment,
  businessUnits,
  cities,
  citiesByBu = [],
  pieceCategories,
  isCOD,
  serviceOptions = [],
  onChangeGroup,
  onChangePiece,
  onAddPiece,
  onRemovePiece,
  validatePiece,
  onRequestMerge,
  onCollapsed,
  open: controlledOpen,
  onToggle,
  phonePrefixList = [],
  favoriteDestinations = [],
  favsByRif,
}) => {
  const isDup = group.isDuplicateInDB
  const hasError = Object.keys(errors).length > 0 || isDup
  const [internalOpen, setInternalOpen] = useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const dbCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Solo re-consultamos la BD cuando el operador EDITA manualmente el número de guía.
  // En la carga inicial el `isDuplicateInDB` ya viene resuelto por el chequeo batcheado
  // (lotes de 5000, POST /shipment/existing) de handleFileSubmit, así que NO disparamos
  // un llamado por fila al montar: con N guías serían N requests innecesarios (100k guías
  // → 100k llamadas). Arranca en false; solo el onChange del input lo activa.
  const userEditedRef = useRef(false)
  const user = useAppSelector((s) => s.user)

  useEffect(() => {
    if (!userEditedRef.current) return
    const tracking = group.shipmentHeaderReference.trim()
    if (dbCheckTimer.current) clearTimeout(dbCheckTimer.current)
    if (!tracking) {
      onChangeGroup({ isDuplicateInDB: false })
      return
    }
    dbCheckTimer.current = setTimeout(async () => {
      try {
        const accountSiteCode = user.client?.accountCode ?? ''
        const accountId = user.client?.id ?? ''
        const accountScope: {
          account_site_code?: string
          account_id?: string
        } = accountSiteCode
          ? { account_site_code: accountSiteCode }
          : accountId
            ? { account_id: accountId }
            : {}
        const res = await getShipmentsExisting({
          shipment_numbers: [tracking],
          ...accountScope,
        })
        const exists = (res.model?.existing ?? []).some(
          (s) => String(s).trim().toUpperCase() === tracking.toUpperCase()
        )
        onChangeGroup({ isDuplicateInDB: exists })
      } catch (_) {}
    }, 500)
    return () => {
      if (dbCheckTimer.current) clearTimeout(dbCheckTimer.current)
    }
  }, [group.shipmentHeaderReference, user.client?.accountCode, user.client?.id])

  const consigneeDocTypeObj = useMemo(
    () =>
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId?.toString() === group.consigneeDocType
      ),
    [identificationTypes, group.consigneeDocType]
  )
  const shipperDocTypeObj = useMemo(
    () =>
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId?.toString() === group.shipperDocType
      ),
    [identificationTypes, group.shipperDocType]
  )
  const cityObj = useMemo(
    () => cities.find((c) => c.locationCode === group.destinationCity),
    [cities, group.destinationCity]
  )
  const buObj = useMemo(
    () =>
      businessUnits.find(
        (b) => String(b.buCode ?? '') === String(group.storeCode ?? '')
      ),
    [businessUnits, group.storeCode]
  )

  const [consigneeDocSearch, setConsigneeDocSearch] = useState(
    consigneeDocTypeObj?.abbreviationName || ''
  )
  const [shipperDocSearch, setShipperDocSearch] = useState(
    shipperDocTypeObj?.abbreviationName || ''
  )
  const [citySearch, setCitySearch] = useState(cityObj?.locationName || '')
  const [buSearch, setBuSearch] = useState(
    buObj
      ? `${buObj.buCode} - ${buObj.location?.name || buObj.buName || (buObj as any).buArea || ''}`
      : ''
  )
  const [deliverySearch, setDeliverySearch] = useState(
    typeShipment.find((t) => t.value === group.deliveryType)?.name || ''
  )

  // Oficinas origen del contrato (multi-oficina). Se lee directo del store (ya
  // está `user` en scope) para no prop-drillear.
  const originBusinessUnits = (user.originBusinessUnits ?? []) as OptionLike[]
  const originLabel = (b?: OptionLike) =>
    b ? `${b.buCode} - ${b.location?.name || b.buName || ''}` : ''
  const originBuObj = useMemo(
    () =>
      originBusinessUnits.find((b) => b.buCode === group.originStoreCode) ??
      (originBusinessUnits.length === 1 ? originBusinessUnits[0] : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [originBusinessUnits, group.originStoreCode]
  )
  const [originSearch, setOriginSearch] = useState(originLabel(originBuObj))

  const headerBg = group.coordWarning
    ? 'bg-amber-50 border-amber-300'
    : hasError
      ? 'bg-red-50 border-red-300'
      : 'bg-gray-50 border-gray-200'

  const errorBadge = group.coordWarning
    ? 'Coordenadas fuera de zona'
    : Object.keys(errors).length > 0
      ? `Con errores: ${Object.keys(errors).length}`
      : isDup
        ? 'Guía existente'
        : null

  const dupBadgeStyle = 'bg-red-100 text-red-700'

  const totalPieceCount = group.pieces.reduce(
    (s, p) => s + (p.quantity || 1),
    0
  )

  const isHome = group.deliveryType === DeliveryEnum.DELIVERY
  const isOffice = group.deliveryType === DeliveryEnum.OFFICE
  const hasMapsCoords =
    isHome && group.latitude != null && group.longitude != null
  useEffect(() => {
    if (!group.storeCode) {
      setBuSearch('')
      return
    }
    const bu = businessUnits.find(
      (b) => String(b.buCode ?? '') === String(group.storeCode ?? '')
    )
    setBuSearch(
      bu
        ? `${bu.buCode} - ${bu.location?.name || bu.buName || (bu as any).buArea || ''}`
            .replace(/^ - /, '')
            .trim()
        : group.storeCode
    )
  }, [group.storeCode, businessUnits])

  useEffect(() => {
    if (!group.destinationCity) {
      setCitySearch('')
      return
    }
    const city = cities.find((c) => c.locationCode === group.destinationCity)
    setCitySearch(city?.locationName || group.destinationCity)
  }, [group.destinationCity, cities])

  const handleDeliveryTypeChange = (val: string) => {
    const name = typeShipment.find((t) => t.value === val)?.name || ''
    setDeliverySearch(name)
    const patch: Partial<ShipmentGroup> = { deliveryType: val }
    if (val !== DeliveryEnum.DELIVERY) {
      patch.latitude = null
      patch.longitude = null
    }
    if (group.importWarning) {
      patch.importWarning = undefined
    }
    onChangeGroup(patch)
  }

  const phoneCode = useMemo(() => {
    if (!group.consigneePhone) return ''
    return obtenerCodigoArea(group.consigneePhone)
  }, [group.consigneePhone])
  const phoneNumberOnly = useMemo(
    () => obtenerNumero(group.consigneePhone || ''),
    [group.consigneePhone]
  )
  const [phoneCodeSearch, setPhoneCodeSearch] = useState(phoneCode)
  useEffect(() => {
    setPhoneCodeSearch(phoneCode)
  }, [phoneCode])

  const setPhoneCombined = (code: string, num: string) => {
    const cleaned = (num || '').replace(/\D/g, '')
    if (!code) {
      onChangeGroup({ consigneePhone: cleaned })
      return
    }
    if (code === 'FIJO') {
      const withZero = cleaned.startsWith('0')
        ? cleaned
        : cleaned
          ? '0' + cleaned
          : ''
      onChangeGroup({ consigneePhone: withZero })
      return
    }
    onChangeGroup({ consigneePhone: code + cleaned })
  }

  const phoneInputMaxLen = phoneCodeSearch === 'FIJO' ? 11 : 7

  const [applyingFav, setApplyingFav] = useState(false)
  const [favWarning, setFavWarning] = useState<string>('')
  const [mapsSelected, setMapsSelected] = useState<{
    name: string
    address: string
    postalCode: string
    coordinates: { lat: number; lng: number }
  }>({ name: '', address: '', postalCode: '', coordinates: { lat: 0, lng: 0 } })
  const reqIdRef = useRef(0)

  const handleSelectDestination = async (fav: DestinationInterface) => {
    const myReq = ++reqIdRef.current
    setApplyingFav(true)
    setFavWarning('')
    try {
      if (!fav.isHomeDelivery) {
        const buCodeWanted = fav.businessUnit?.buCode || fav.store?.code
        if (buCodeWanted) {
          const buMatch = businessUnits.find(
            (b: any) => b.buCode === buCodeWanted
          )
          if (buMatch && (buMatch as any).isLocked === true) {
            if (myReq === reqIdRef.current) {
              setFavWarning(
                `Tienda destino ${buCodeWanted} está bloqueada. Selecciona otro favorito.`
              )
            }
            return
          }
        }
      }
      const result = await applyDestinationFromFavorite(
        fav,
        getStoreByCoverageDjango
      )
      if (myReq !== reqIdRef.current) return
      const patch = group.importWarning
        ? { ...result.patch, importWarning: undefined }
        : result.patch
      onChangeGroup(patch)
      if (result.warning) setFavWarning(result.warning)
    } finally {
      if (myReq === reqIdRef.current) setApplyingFav(false)
    }
  }

  const handleClearFavorite = () => {
    ++reqIdRef.current
    setApplyingFav(false)
    onChangeGroup(clearConsigneeFields())
    setPhoneCodeSearch('')
    setFavWarning('')
  }

  const hasFavorite = !!group.favoriteDestinationId
  const favLockDisabled = hasFavorite || applyingFav
  const favLockClass = hasFavorite ? 'bg-gray-50' : ''

  // Domicilio: la "Dirección destino *" (requerida) se puede escribir a mano cuando NO
  // hay una dirección canónica de Maps que proteger — es decir, sin coordenadas (o con
  // coords que no geocodificaron) — y la fila no está bloqueada por un favorito. Así, si
  // el operador solo eligió ciudad, igual puede tipear la dirección. Con coordenadas +
  // dirección resuelta queda readonly (Maps es la fuente de verdad).
  const canEditAddressManually =
    !favLockDisabled && !(hasMapsCoords && !!group.destinationAddress?.trim())

  const favoriteHasInvalidData =
    hasFavorite &&
    (!!errors.consigneePhone ||
      !!errors.consigneeEmail ||
      !!errors.consigneeDocNumber ||
      !!errors.consigneeDocType ||
      !!errors.consigneeName)

  const lastLookedUpCoordsRef = useRef<string>('')
  useEffect(() => {
    if (!isHome) return
    if (group.latitude == null || group.longitude == null) return
    if (hasFavorite || applyingFav) return
    const coordKey = `${group.latitude},${group.longitude}`
    if (coordKey === lastLookedUpCoordsRef.current) return

    const myReq = ++reqIdRef.current
    const coverageController = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const [res, geo] = await Promise.all([
          getStoreByCoverageDjango(
            group.longitude as number,
            group.latitude as number,
            coverageController.signal
          ),
          reverseGeocodeCoords(
            group.latitude as number,
            group.longitude as number
          ),
        ])
        if (myReq !== reqIdRef.current) return
        if (res.didError || !res.model) {
          lastLookedUpCoordsRef.current = coordKey
          onChangeGroup({ coordWarning: COORD_OUT_OF_ZONE_MSG })
          return
        }
        lastLookedUpCoordsRef.current = coordKey
        const loc = res.model.location || {}
        onChangeGroup({
          destinationAddress: geo?.address || group.destinationAddress,
          destinationCity: loc.locationCode || group.destinationCity,
          storeCode: res.model.buCode || group.storeCode,
          postalCode: geo?.postalCode || loc.postalCode || group.postalCode,
          coordWarning: undefined,
        })
      } catch (_) {}
    }, 500)
    return () => {
      clearTimeout(timer)
      coverageController.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.latitude, group.longitude, isHome, hasFavorite, applyingFav])

  const suggestedFavorite = useMemo<DestinationInterface | undefined>(() => {
    if (group.favoriteDestinationId) return undefined
    if (group.optedOutOfFavorite) return undefined
    const rif = (group.consigneeDocNumber || '').trim()
    if (!rif || !favsByRif) return undefined
    return favsByRif.get(rif)
  }, [
    group.consigneeDocNumber,
    group.favoriteDestinationId,
    group.optedOutOfFavorite,
    favsByRif,
  ])

  return (
    <div className="mb-2 border border-gray-200 rounded-lg overflow-hidden transition-all duration-300">
      {/* Header */}
      <button
        type="button"
        className={`w-full flex items-center justify-between px-4 py-3 text-left ${headerBg}`}
        onClick={() => {
          if (isControlled) {
            onToggle?.()
          } else {
            if (open) onCollapsed?.()
            setInternalOpen((o) => !o)
          }
        }}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {group.coordWarning ? (
            <ExclamationTriangleIcon
              className="w-4 h-4 text-red-500 flex-shrink-0"
              aria-hidden="true"
              aria-label="Error de coordenadas"
            />
          ) : hasError ? (
            <ExclamationTriangleIcon
              className="w-4 h-4 text-red-500 flex-shrink-0"
              aria-hidden="true"
            />
          ) : (
            <CheckCircleIcon
              className="w-4 h-4 text-green-600 flex-shrink-0"
              aria-hidden="true"
              aria-label="Guía válida"
            />
          )}
          <span className="font-mono font-medium text-sm truncate">
            {group.shipmentHeaderReference || '(sin tracking)'}
          </span>
          <span className="text-gray-400 text-sm hidden sm:inline">•</span>
          <span className="text-gray-600 text-sm truncate hidden sm:inline">
            {group.consigneeName || '—'}
          </span>
          {group.favoriteDestinationId && (
            <span
              className="bg-main-50 text-main-500 px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1 flex-shrink-0"
              title={`Destinatario favorito aplicado: ${group.consigneeName || ''}`}
            >
              <StarIcon className="w-3 h-3" aria-hidden="true" />
              <span className="hidden md:inline">Destinatario Favorito</span>
              <span className="sr-only">
                Destinatario favorito aplicado: {group.consigneeName || ''}
              </span>
            </span>
          )}
          <span className="text-gray-400 text-sm hidden md:inline">•</span>
          <span className="text-gray-500 text-sm hidden md:inline">
            {totalPieceCount} pieza(s)
          </span>
        </div>
        {errorBadge && (
          <span
            className={`ml-2 flex-shrink-0 text-xs font-medium ${dupBadgeStyle} px-2 py-0.5 rounded`}
          >
            {errorBadge}
          </span>
        )}
      </button>

      {/* Panel expandido */}
      {open && (
        <div className="p-4 space-y-6 bg-white">
          {/* G.4-bis: import-time warning banner (e.g., favorite override changed deliveryType) */}
          {group.importWarning && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2"
              role="status"
              aria-live="polite"
            >
              <ExclamationTriangleIcon
                className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-xs text-amber-800 flex-1">
                {group.importWarning}
              </p>
              <button
                type="button"
                onClick={() => onChangeGroup({ importWarning: undefined })}
                className="text-amber-700 hover:text-amber-900 text-xs underline flex-shrink-0"
                aria-label="Cerrar aviso"
              >
                Cerrar
              </button>
            </div>
          )}

          {/* SECCIÓN 1: Datos del Envío */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Datos del Envío
            </h3>
            {/* Tracking — primer campo */}
            <div className="mb-4">
              <FormText
                id={`shipmentHeaderReference-${index}`}
                name={`shipmentHeaderReference-${index}`}
                label="Número de guía aliada *"
                value={group.shipmentHeaderReference}
                maxLength={30}
                onChange={(e: any) => {
                  const v = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9-]/g, '')
                  // Marca edición manual: habilita el re-chequeo por fila del useEffect.
                  userEditedRef.current = true
                  onChangeGroup({ shipmentHeaderReference: v })
                }}
                error={errors.shipmentHeaderReference}
              />
              {errors.shipmentHeaderReference?.includes(
                'duplicado en esta carga masiva'
              ) &&
                onRequestMerge && (
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onRequestMerge}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Combinar grupos
                    </button>
                  </div>
                )}
              {isDup && !errors.shipmentHeaderReference && (
                <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                  Esta guía ya existe en el sistema. Edita el número antes de
                  enviar.
                </p>
              )}
            </div>

            {serviceOptions.length > 1 ? (
              <div className="mb-4">
                <FormSelect<ServiceDTO>
                  id={`service-${index}`}
                  name={`service-${index}`}
                  label="Servicio *"
                  selected={serviceOptions.find(
                    (s) => s.serviceID === group.serviceId
                  )}
                  options={serviceOptions}
                  optionString={(s) => s.serviceShortName}
                  onSelectOption={(s) =>
                    onChangeGroup({ serviceId: s.serviceID })
                  }
                />
              </div>
            ) : serviceOptions.length === 1 ? (
              <div className="mb-4">
                <FormText
                  id={`service-${index}`}
                  name={`service-${index}`}
                  label="Servicio"
                  value={serviceOptions[0].serviceShortName}
                  disabled
                  className="bg-gray-50"
                  onChange={() => {}}
                />
              </div>
            ) : null}

            {/* Cambio J.2: Domicilio gets explicit layout per operator's mental model:
                Row 1: Tipo de entrega + Búsqueda Google Maps (paired)
                Row 2: Coordenadas section (lat/lng, full-width gray panel)
                Row 3: Ciudad + Tienda asignada (both Maps-derived, locked)
                Row 4: Dirección destino * (readonly textarea showing canonical Maps result)
                Row 5: Código postal
                Row 6: Punto de referencia (delivery hint)
                Office keeps its original grid (Tipo + Código tienda → Dir → Postal). */}
            {isHome ? (
              <>
                {/* Row 1: Tipo de entrega + Búsqueda en Google Maps */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSearch
                    id={`deliveryType-${index}`}
                    name={`deliveryType-${index}`}
                    label="Tipo de entrega *"
                    value={deliverySearch}
                    options={typeShipment}
                    error={errors.deliveryType}
                    onChange={(e: any) => setDeliverySearch(e.target.value)}
                    onSelectOption={(opt: any) =>
                      handleDeliveryTypeChange(opt?.value || '')
                    }
                    onFilter={(opt: any, filter: string) =>
                      (opt.name || '')
                        .toLowerCase()
                        .includes(filter.toLowerCase())
                    }
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">{option.name}</div>
                    )}
                    disabled={favLockDisabled}
                    className={favLockClass}
                  />
                  <FormMapsAutocomplete
                    id={`mapsSearch-${index}`}
                    name={`mapsSearch-${index}`}
                    label="Búsqueda en google maps"
                    placeholder="Buscar dirección para cambiarla…"
                    region={AutocompleteRegion.TEALCA}
                    selected={mapsSelected}
                    onSelectLocation={(loc, bu) => {
                      // Reflejar lo elegido en el campo (estado local) y aplicar al grupo.
                      setMapsSelected({
                        name: loc.address || '',
                        address: loc.address || '',
                        postalCode: loc.postalCode || '',
                        coordinates: {
                          lat: loc.coordinates?.lat ?? 0,
                          lng: loc.coordinates?.lng ?? 0,
                        },
                      })
                      onChangeGroup({
                        destinationAddress:
                          loc.address || group.destinationAddress,
                        postalCode: loc.postalCode || group.postalCode,
                        latitude: loc.coordinates?.lat ?? group.latitude,
                        longitude: loc.coordinates?.lng ?? group.longitude,
                        destinationCity:
                          bu?.location?.locationCode || group.destinationCity,
                        storeCode: bu?.buCode || group.storeCode,
                        // Una selección válida de Maps limpia cualquier aviso de coords.
                        coordWarning: undefined,
                      })
                    }}
                    disabled={favLockDisabled}
                  />
                </div>

                {/* Row 2: Coordenadas — gray panel. Editing lat/lng triggers J.1 territory lookup.
                    Per-row coordWarning (set by validateCoordsInBatches when territory check
                    fails) renders below the inputs so the operator sees the explanation right
                    where they fix it. Auto-clears on edit so the J.1 re-lookup can succeed. */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 mb-3">
                    Coordenadas de entrega — opcional, solo para domicilio
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormText
                      id={`latitude-${index}`}
                      name={`latitude-${index}`}
                      label="Latitud"
                      value={
                        group.latitude !== null ? String(group.latitude) : ''
                      }
                      placeholder="Ej: 10.4806"
                      onChange={(e: any) => {
                        const v = e.target.value.replace(',', '.')
                        const parsed = parseFloat(v)
                        onChangeGroup({
                          latitude: isNaN(parsed) ? null : parsed,
                          ...(group.coordWarning
                            ? { coordWarning: undefined }
                            : {}),
                        })
                      }}
                      error={errors.latitude}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                    <FormText
                      id={`longitude-${index}`}
                      name={`longitude-${index}`}
                      label="Longitud"
                      value={
                        group.longitude !== null ? String(group.longitude) : ''
                      }
                      placeholder="Ej: -66.9147"
                      onChange={(e: any) => {
                        const v = e.target.value.replace(',', '.')
                        const parsed = parseFloat(v)
                        onChangeGroup({
                          longitude: isNaN(parsed) ? null : parsed,
                          ...(group.coordWarning
                            ? { coordWarning: undefined }
                            : {}),
                        })
                      }}
                      error={errors.longitude}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                  </div>
                  {group.coordWarning && (
                    <div
                      className="mt-3 flex items-start gap-2 text-xs bg-red-50 border border-red-200 rounded-md px-3 py-2"
                      role="alert"
                      aria-live="polite"
                    >
                      <ExclamationTriangleIcon
                        className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                        aria-hidden="true"
                      />
                      <p className="text-red-800 flex-1">
                        {group.coordWarning}
                      </p>
                    </div>
                  )}
                </div>

                {/* Row 3: Ciudad + Tienda asignada.
                    Ciudad: locked only when BOTH coords AND a store were resolved
                    by Maps/territory. If coords didn't resolve a store (territory
                    returned no BU, or only city + no store), the operator can
                    still pick a city manually — and picking the city derives the
                    storeCode from citiesByBu so the row becomes submittable. */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <FormSearch
                    id={`destinationCity-${index}`}
                    name={`destinationCity-${index}`}
                    label={
                      hasMapsCoords && group.storeCode
                        ? 'Ciudad destino * (asignada por Maps)'
                        : 'Ciudad destino *'
                    }
                    value={citySearch}
                    options={cities}
                    error={errors.destinationCity}
                    disabled={
                      (hasMapsCoords && !!group.storeCode) || favLockDisabled
                    }
                    className={hasFavorite ? favLockClass : ''}
                    onChange={(e: any) => setCitySearch(e.target.value)}
                    onSelectOption={(opt: any) => {
                      const locationCode = opt?.locationCode || ''
                      // Derive storeCode from citiesByBu lookup so picking a city
                      // resolves "Tienda asignada" too — required when coords are
                      // missing or didn't yield a store.
                      const derivedBuCode =
                        citiesByBu.find((c) => c.locationCode === locationCode)
                          ?.buCode || ''
                      // Pull postal code from the resolved BU if we have one.
                      const derivedBu = derivedBuCode
                        ? businessUnits.find(
                            (b) =>
                              String(b.buCode ?? '') === String(derivedBuCode)
                          )
                        : undefined
                      onChangeGroup({
                        destinationCity: locationCode,
                        storeCode: derivedBuCode || group.storeCode,
                        postalCode:
                          derivedBu?.location?.postalCode || group.postalCode,
                      })
                      setCitySearch(opt?.locationName || '')
                    }}
                    onFilter={(opt: any, filter: string) =>
                      (opt.locationName || '')
                        .toLowerCase()
                        .includes(filter.toLowerCase())
                    }
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">
                        {option.locationName}
                      </div>
                    )}
                  />
                  <FormText
                    id={`assignedStore-${index}`}
                    name={`assignedStore-${index}`}
                    label="Tienda asignada"
                    value={
                      buObj
                        ? `${buObj.buCode} - ${buObj.location?.name || buObj.buName || (buObj as any).buArea || ''}`
                            .replace(/^ - /, '')
                            .trim()
                        : group.storeCode || ''
                    }
                    disabled
                    placeholder="Se asignará automáticamente por las coordenadas / ciudad"
                    className="bg-gray-50"
                    onChange={() => {}}
                  />
                </div>

                {/* Row 4: Dirección destino — dirección canónica de Maps (readonly cuando
                    hay coordenadas) o entrada manual cuando solo se eligió ciudad. */}
                <div className="mt-4">
                  <FormTextArea
                    id={`destinationAddress-${index}`}
                    name={`destinationAddress-${index}`}
                    label="Dirección destino *"
                    value={group.destinationAddress || ''}
                    disabled={!canEditAddressManually}
                    className={canEditAddressManually ? '' : 'bg-gray-50'}
                    placeholder={
                      canEditAddressManually
                        ? 'Escribe la dirección de entrega'
                        : 'Selecciona una dirección en Google Maps o ingresa coordenadas'
                    }
                    error={errors.destinationAddress}
                    onChange={(e: any) =>
                      onChangeGroup({ destinationAddress: e.target.value })
                    }
                  />
                </div>

                {/* Row 5: Código postal */}
                <div className="mt-4">
                  <FormText
                    id={`postalCode-${index}`}
                    name={`postalCode-${index}`}
                    label="Código postal"
                    value={group.postalCode}
                    onChange={(e: any) =>
                      onChangeGroup({ postalCode: e.target.value })
                    }
                    disabled={favLockDisabled}
                    className={favLockClass}
                  />
                </div>

                {/* Row 6: Punto de referencia — delivery hint for the courier */}
                <div className="mt-4">
                  <FormText
                    id={`addressReference-${index}`}
                    name={`addressReference-${index}`}
                    label="Punto de referencia"
                    placeholder='Ej: "Frente al parque", "Portón negro"'
                    value={group.addressReference || ''}
                    onChange={(e: any) =>
                      onChangeGroup({ addressReference: e.target.value })
                    }
                    disabled={favLockDisabled}
                    className={favLockClass}
                  />
                </div>
              </>
            ) : (
              /* Office: original grid layout (Tipo + Código tienda → Dirección → Postal) */
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormSearch
                  id={`deliveryType-${index}`}
                  name={`deliveryType-${index}`}
                  label="Tipo de entrega *"
                  value={deliverySearch}
                  options={typeShipment}
                  error={errors.deliveryType}
                  onChange={(e: any) => setDeliverySearch(e.target.value)}
                  onSelectOption={(opt: any) =>
                    handleDeliveryTypeChange(opt?.value || '')
                  }
                  onFilter={(opt: any, filter: string) =>
                    (opt.name || '')
                      .toLowerCase()
                      .includes(filter.toLowerCase())
                  }
                  RenderOption={({ option }: any) => (
                    <div className="px-2 py-1 text-sm">{option.name}</div>
                  )}
                  disabled={favLockDisabled}
                  className={favLockClass}
                />
                <FormSearch
                  id={`storeCode-${index}`}
                  name={`storeCode-${index}`}
                  label="Código de tienda *"
                  value={buSearch}
                  options={businessUnits}
                  error={errors.storeCode}
                  onChange={(e: any) => setBuSearch(e.target.value)}
                  onSelectOption={(opt: any) => {
                    const loc = opt?.location || {}
                    onChangeGroup({
                      storeCode: opt?.buCode || '',
                      destinationCity: loc.locationCode || '',
                      destinationAddress:
                        loc.address || group.destinationAddress,
                      postalCode: loc.postalCode || group.postalCode,
                    })
                    setBuSearch(
                      opt
                        ? `${opt.buCode || ''} - ${loc.name || opt.buName || ''}`
                            .replace(/^ - /, '')
                            .trim()
                        : ''
                    )
                  }}
                  onFilter={(opt: any, filter: string) => {
                    // Filtrar contra el LABEL ("buCode - nombre"), no campo por campo:
                    // al reentrar, el value es el label completo y filterByName no lo
                    // matcheaba → "Elementos no encontrados". Mismo fix que Oficina origen.
                    const q = (filter || '').toLowerCase().trim()
                    if (q === '') return true
                    const label = `${opt.buCode || ''} - ${
                      opt.location?.name || opt.buName || opt.buArea || ''
                    }`.toLowerCase()
                    return label.includes(q)
                  }}
                  RenderOption={({ option }: any) => (
                    <div className="px-2 py-1 text-sm">
                      <p className="font-medium">
                        {option.buCode} —{' '}
                        {option.location?.name || option.buName}
                      </p>
                      {option.location?.address && (
                        <p className="text-xs text-gray-500">
                          {option.location.address}
                        </p>
                      )}
                    </div>
                  )}
                  disabled={favLockDisabled}
                  className={favLockClass}
                />
                <FormText
                  id={`destinationAddress-${index}`}
                  name={`destinationAddress-${index}`}
                  label="Dirección destino *"
                  value={group.destinationAddress}
                  onChange={(e: any) =>
                    onChangeGroup({ destinationAddress: e.target.value })
                  }
                  error={errors.destinationAddress}
                  disabled
                  className="bg-gray-50"
                />
                <FormText
                  id={`postalCode-${index}`}
                  name={`postalCode-${index}`}
                  label="Código postal"
                  value={group.postalCode}
                  onChange={(e: any) =>
                    onChangeGroup({ postalCode: e.target.value })
                  }
                  disabled={favLockDisabled}
                  className={favLockClass}
                />
              </div>
            )}

            <div className="mt-4">
              <FormTextArea
                id={`notes-${index}`}
                name={`notes-${index}`}
                label="Observaciones"
                value={group.notes}
                onChange={(e: any) => onChangeGroup({ notes: e.target.value })}
              />
            </div>
          </div>

          {/* SECCIÓN 2: Remitente — oculto en COD */}
          {!isCOD && (
            <div>
              <hr className="my-3"></hr>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                Remitente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormSearch
                  id={`shipperDocType-${index}`}
                  name={`shipperDocType-${index}`}
                  label="Tipo de documento *"
                  value={shipperDocSearch}
                  options={identificationTypes}
                  error={errors.shipperDocType}
                  onChange={(e: any) => setShipperDocSearch(e.target.value)}
                  onSelectOption={(opt: any) => {
                    onChangeGroup({
                      shipperDocType:
                        opt?.taxIdentificationTypeId?.toString() || '',
                    })
                    setShipperDocSearch(opt?.abbreviationName || '')
                  }}
                  onFilter={(opt: any, filter: string) =>
                    filterByName(opt, filter)
                  }
                  RenderOption={({ option }: any) => (
                    <div className="px-2 py-1 text-sm">
                      {option.abbreviationName}
                    </div>
                  )}
                />
                <FormText
                  id={`shipperDocNumber-${index}`}
                  name={`shipperDocNumber-${index}`}
                  label="Nº Documento *"
                  value={group.shipperDocNumber}
                  onChange={(e: any) => {
                    const v = e.target.value.replace(/\D/g, '')
                    onChangeGroup({ shipperDocNumber: v })
                  }}
                  error={errors.shipperDocNumber}
                />
                <FormText
                  id={`shipperName-${index}`}
                  name={`shipperName-${index}`}
                  label="Nombre *"
                  value={group.shipperName}
                  onChange={(e: any) =>
                    onChangeGroup({ shipperName: e.target.value })
                  }
                  error={errors.shipperName}
                />
              </div>
            </div>
          )}

          {/* SECCIÓN 2b: Oficina origen — visible también bajo COD (el origen es
              de dónde sale la guía, independiente del remitente). Solo multi-oficina
              muestra combo; con 1 sola, solo lectura; con 0, no renderiza. */}
          {originBusinessUnits.length > 0 && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {originBusinessUnits.length > 1 ? (
                  <FormSearch
                    id={`originStoreCode-${index}`}
                    name={`originStoreCode-${index}`}
                    label="Oficina origen *"
                    value={originSearch}
                    options={originBusinessUnits}
                    error={errors.originStoreCode}
                    onChange={(e: any) => setOriginSearch(e.target.value)}
                    onSelectOption={(opt: any) => {
                      onChangeGroup({ originStoreCode: opt?.buCode || '' })
                      setOriginSearch(originLabel(opt))
                    }}
                    onFilter={(opt: any, filter: string) => {
                      // Filtrar contra el LABEL ("buCode - nombre"), no campo por campo:
                      // así el query parcial matchea Y al reentrar con el label completo
                      // (value = "MIA - Miami") la opción seleccionada sigue apareciendo.
                      const q = (filter || '').toLowerCase().trim()
                      return q === '' || originLabel(opt).toLowerCase().includes(q)
                    }}
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">
                        {originLabel(option)}
                      </div>
                    )}
                  />
                ) : (
                  <FormText
                    id={`originStoreCode-ro-${index}`}
                    name={`originStoreCode-ro-${index}`}
                    label="Oficina origen"
                    value={originLabel(originBuObj)}
                    disabled
                    readOnly
                    onChange={() => {}}
                  />
                )}
              </div>
            </div>
          )}

          {/* SECCIÓN 3: Destinatario */}
          <div>
            <hr className="my-3"></hr>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Destinatario
            </h3>

            {/* V3+E: Favorite picker (rich UI) */}
            <div className="mb-4">
              <FavoriteDestinationPicker
                id={`fav-picker-${index}`}
                favorites={favoriteDestinations}
                selectedId={group.favoriteDestinationId}
                selectedDisplay={group.consigneeName}
                disabled={applyingFav}
                loading={applyingFav}
                onSelect={handleSelectDestination}
                onClear={handleClearFavorite}
              />
              {favWarning && (
                <p
                  className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1"
                  role="status"
                  aria-live="polite"
                >
                  <ExclamationTriangleIcon
                    className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                  <span>{favWarning}</span>
                </p>
              )}
            </div>

            {/* V3: RIF auto-suggest banner */}
            {suggestedFavorite && !applyingFav && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3 flex items-center justify-between text-xs gap-2 flex-wrap">
                <span className="text-amber-800 flex items-center gap-1 flex-1 min-w-0">
                  <StarIcon className="w-3.5 h-3.5 flex-shrink-0 text-amber-600" />
                  <span className="truncate">
                    Coincide con favorito:{' '}
                    <strong>
                      {suggestedFavorite.account?.accountFullName}
                    </strong>
                  </span>
                </span>
                <button
                  type="button"
                  className="text-blue-600 hover:text-blue-800 font-medium px-3 py-2 rounded hover:bg-amber-100"
                  onClick={() => handleSelectDestination(suggestedFavorite)}
                >
                  Aplicar favorito
                </button>
              </div>
            )}

            {/* H.3c: when favorite is applied, lock destinatario fields to keep them
                in sync with the stored favorite. Operator clears the favorite via the
                picker's X button to unlock. */}
            {hasFavorite && !favoriteHasInvalidData && (
              <div
                className="mb-3 text-xs text-gray-600 flex items-center gap-2"
                role="status"
              >
                <StarIcon
                  className="w-3.5 h-3.5 text-main-500 flex-shrink-0"
                  aria-hidden="true"
                />
                <span>
                  No se puede modificar la información porque son datos de un
                  destinatario favorito. Para editar, limpia la selección con el
                  botón ×.
                </span>
              </div>
            )}

            {/* Favorito con datos inválidos para el sistema (ej. teléfono que no
                cumple el formato del backend). Los campos están bloqueados a propósito:
                editarlos acá NO actualiza el favorito. Le indicamos al operador que lo
                corrija en su libreta de Destinos favoritos, o que lo limpie (×) para
                cargar un valor solo para esta guía. */}
            {favoriteHasInvalidData && (
              <div
                className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2"
                role="alert"
              >
                <ExclamationTriangleIcon
                  className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="text-xs text-red-800 flex-1 space-y-1">
                  <p className="font-semibold">
                    Este destinatario favorito tiene datos inválidos
                    {errors.consigneePhone ? ' (teléfono)' : ''}.
                  </p>
                  {errors.consigneePhone && <p>{errors.consigneePhone}</p>}
                  <p>
                    Modificá el favorito en <strong>Destinos favoritos</strong>{' '}
                    antes de cargarlo, o limpiá la selección con el botón{' '}
                    <strong>×</strong> para ingresar otro número solo en esta
                    guía (no se guardará en el favorito).
                  </p>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormSearch
                id={`consigneeDocType-${index}`}
                name={`consigneeDocType-${index}`}
                label="Tipo de documento *"
                value={consigneeDocSearch}
                options={identificationTypes}
                error={errors.consigneeDocType}
                onChange={(e: any) => setConsigneeDocSearch(e.target.value)}
                onSelectOption={(opt: any) => {
                  onChangeGroup({
                    consigneeDocType:
                      opt?.taxIdentificationTypeId?.toString() || '',
                  })
                  setConsigneeDocSearch(opt?.abbreviationName || '')
                }}
                onFilter={(opt: any, filter: string) =>
                  filterByName(opt, filter)
                }
                RenderOption={({ option }: any) => (
                  <div className="px-2 py-1 text-sm">
                    {option.abbreviationName}
                  </div>
                )}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              <FormText
                id={`consigneeDocNumber-${index}`}
                name={`consigneeDocNumber-${index}`}
                label="Nº Documento *"
                value={group.consigneeDocNumber}
                onChange={(e: any) => {
                  const v = e.target.value.replace(/\D/g, '')
                  onChangeGroup({ consigneeDocNumber: v })
                }}
                error={errors.consigneeDocNumber}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              <FormText
                id={`consigneeName-${index}`}
                name={`consigneeName-${index}`}
                label="Nombre *"
                value={group.consigneeName}
                onChange={(e: any) =>
                  onChangeGroup({ consigneeName: e.target.value })
                }
                error={errors.consigneeName}
                disabled={favLockDisabled}
                className={favLockClass}
              />
              <div className="md:col-span-1">
                <div className="flex flex-col md:flex-row gap-2">
                  <div className="md:w-32">
                    <FormSearch
                      id={`consigneePhoneCode-${index}`}
                      name={`consigneePhoneCode-${index}`}
                      label="Operadora *"
                      value={phoneCodeSearch}
                      options={phonePrefixList}
                      onChange={(e: any) => setPhoneCodeSearch(e.target.value)}
                      onSelectOption={(opt: any) => {
                        const code = opt?.value || ''
                        setPhoneCodeSearch(code)
                        setPhoneCombined(code, phoneNumberOnly)
                      }}
                      onFilter={(opt: any, filter: string) =>
                        (opt.value || '')
                          .toLowerCase()
                          .includes(filter.toLowerCase()) ||
                        (opt.text || '')
                          .toLowerCase()
                          .includes(filter.toLowerCase())
                      }
                      RenderOption={({ option }: any) => (
                        <div className="px-2 py-1 text-sm">{option.value}</div>
                      )}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                  </div>
                  <div className="flex-1">
                    <FormText
                      id={`consigneePhone-${index}`}
                      name={`consigneePhone-${index}`}
                      label="Teléfono *"
                      value={phoneNumberOnly}
                      maxLength={phoneInputMaxLen}
                      inputMode="numeric"
                      placeholder={
                        phoneCodeSearch === 'FIJO' ? '02121234567' : '1234567'
                      }
                      onChange={(e: any) => {
                        const raw = e.target.value
                        // Try paste detection first (full number with mobile operator)
                        const parsed = tryParsePastedPhone(raw, phonePrefixList)
                        if (parsed) {
                          setPhoneCodeSearch(parsed.code)
                          setPhoneCombined(parsed.code, parsed.number)
                          return
                        }
                        const v = raw
                          .replace(/\D/g, '')
                          .slice(0, phoneInputMaxLen)
                        setPhoneCombined(phoneCodeSearch, v)
                      }}
                      error={errors.consigneePhone}
                      disabled={favLockDisabled}
                      className={favLockClass}
                    />
                  </div>
                </div>
              </div>
              <FormText
                id={`consigneeEmail-${index}`}
                name={`consigneeEmail-${index}`}
                label="Correo"
                value={group.consigneeEmail}
                onChange={(e: any) =>
                  onChangeGroup({ consigneeEmail: e.target.value })
                }
                error={errors.consigneeEmail}
                disabled={favLockDisabled}
                className={favLockClass}
              />
            </div>
          </div>

          {/* SECCIÓN 4: Piezas */}
          <div>
            <hr className="my-4"></hr>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Piezas ({totalPieceCount})
            </h3>
            {errors.pieces && (
              <p className="text-sm text-red-600 mb-2">{errors.pieces}</p>
            )}
            <ShipmentBulkUploadPiecesList
              groupIndex={index}
              pieces={group.pieces}
              pieceCategories={pieceCategories}
              onChangePiece={onChangePiece}
              onAddPiece={onAddPiece}
              onRemovePiece={onRemovePiece}
              validatePiece={(p) => validatePiece(p, group.serviceId)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
